import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { helpdeskAPI } from '../services/api';
import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://agents.matix.one/api/v1',
  timeout: 30000
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('helpdesk_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const MyContracts = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [client, setClient] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const res = await api.get('/helpdesk/my-contracts');
      if (res.data.success) {
        setContracts(res.data.contracts || []);
        setClient(res.data.client || null);
      }
    } catch (err) {
      setError(isRTL ? 'فشل في تحميل العقود' : 'Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  const L = {
    title: isRTL ? 'عقودي' : 'My Contracts',
    subtitle: isRTL ? 'عقود الصيانة والخدمة النشطة' : 'Active maintenance & service contracts',
    noContracts: isRTL ? 'لا توجد عقود نشطة' : 'No active contracts found',
    noAccount: isRTL ? 'لا يوجد حساب خدمة ميدانية مرتبط' : 'No field service account linked',
    visitsRemaining: isRTL ? 'الزيارات المتبقية' : 'Visits Remaining',
    responseTime: isRTL ? 'وقت الاستجابة' : 'Response Time',
    hours: isRTL ? 'ساعات' : 'hours',
    expires: isRTL ? 'ينتهي في' : 'Expires',
    equipment: isRTL ? 'المعدات المغطاة' : 'Covered Equipment',
    requestService: isRTL ? 'طلب خدمة' : 'Request Service',
    viewDetails: isRTL ? 'عرض التفاصيل' : 'View Details',
    active: isRTL ? 'نشط' : 'Active',
    expiringSoon: isRTL ? 'ينتهي قريباً' : 'Expiring Soon',
    expired: isRTL ? 'منتهي' : 'Expired',
  };

  const statusBadge = (health) => {
    const map = {
      active: { bg: '#dcfce7', color: '#16a34a', label: L.active },
      expiring_soon: { bg: '#fef3c7', color: '#d97706', label: L.expiringSoon },
      expired: { bg: '#fee2e2', color: '#dc2626', label: L.expired },
    };
    const s = map[health] || map.active;
    return (
      <span style={{
        display: 'inline-block', padding: '4px 12px', borderRadius: '20px',
        fontSize: '12px', fontWeight: '600', backgroundColor: s.bg, color: s.color
      }}>
        {s.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ direction: isRTL ? 'rtl' : 'ltr', padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '16px', color: '#6b7280' }}>
          {isRTL ? 'جاري التحميل...' : 'Loading...'}
        </div>
      </div>
    );
  }

  return (
    <div style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', margin: 0 }}>
          {L.title}
        </h1>
        <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
          {L.subtitle}
        </p>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: '8px', color: '#dc2626', marginBottom: '16px', fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {contracts.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '60px 20px',
          backgroundColor: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
          <div style={{ fontSize: '16px', color: '#6b7280' }}>
            {client ? L.noContracts : L.noAccount}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {contracts.map(contract => (
            <div key={contract.id} style={{
              backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px',
              padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>
                    {isRTL ? (contract.title_ar || contract.title) : contract.title}
                  </div>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                    {contract.contract_number}
                  </div>
                </div>
                {statusBadge(contract.health_status)}
              </div>

              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '12px', marginBottom: '16px',
                padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px'
              }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', fontWeight: '600' }}>
                    {L.visitsRemaining}
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: contract.visits_remaining > 0 ? '#16a34a' : '#dc2626' }}>
                    {contract.visits_remaining ?? '—'} / {contract.total_visits_allowed ?? '∞'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', fontWeight: '600' }}>
                    {L.responseTime}
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#111827' }}>
                    {contract.sla_response_hours || '—'} <span style={{ fontSize: '12px', color: '#6b7280' }}>{L.hours}</span>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', fontWeight: '600' }}>
                    {L.expires}
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: contract.health_status === 'expiring_soon' ? '#d97706' : '#111827' }}>
                    {contract.end_date ? new Date(contract.end_date).toLocaleDateString(isRTL ? 'ar-EG' : 'en-US') : '—'}
                  </div>
                </div>
              </div>

              {contract.equipment && contract.equipment.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
                    {L.equipment} ({contract.equipment.length})
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {contract.equipment.slice(0, 5).map(eq => (
                      <span key={eq.id} style={{
                        padding: '4px 10px', backgroundColor: '#eff6ff',
                        borderRadius: '6px', fontSize: '12px', color: '#1d4ed8'
                      }}>
                        {isRTL ? (eq.name_ar || eq.name) : eq.name}
                      </span>
                    ))}
                    {contract.equipment.length > 5 && (
                      <span style={{ padding: '4px 10px', fontSize: '12px', color: '#6b7280' }}>
                        +{contract.equipment.length - 5}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => navigate(`/contracts/${contract.id}/request-service`)}
                  style={{
                    padding: '8px 16px', borderRadius: '8px', border: 'none',
                    backgroundColor: '#2563eb', color: '#fff', fontSize: '13px', fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  🔧 {L.requestService}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyContracts;
